/**
 * `<html lang>` — one attribute, and two things that legitimately decide it.
 *
 * WHY THIS IS NOT ONE LINE IN LanguageContext. It was, and on the one screen that matters most it was
 * wrong. The application resolves its language through the precedence chain (account -> device choice
 * -> device -> tenant default -> English). THE QR STICKER SHEET DOES NOT: the person holding it has no
 * account, and the languages on offer are the ones THAT SHEET is written in — a set derived from the
 * tenant's translation rows, defaulted by `public_default_language`, which is a different setting from
 * the company language that serves people who sign in. So the sheet renders Spanish while the
 * application chain, reading nothing, answers English, and the attribute described the wrong document.
 *
 * WHAT THAT COSTS. `lang` is what a screen reader reads the page's phonemes from. Spanish safety
 * questions announced with English phonemes are close to unintelligible, and the population this
 * attribute serves on that page — an anonymous hand at a machine, no login, one bar of signal — is
 * exactly the population the whole feature exists for. It is also the one page where a wrong answer is
 * guaranteed rather than possible: nothing on it ever writes the application's own language key.
 *
 * THE RULE: A PAGE THAT DECIDES ITS OWN LANGUAGE OUTRANKS THE APPLICATION CHAIN, for as long as it is
 * on screen. Not a subscription and not React state, because both ends of this are effects and React
 * runs a child's effect BEFORE its parent's — so a provider above `<App/>` would otherwise overwrite,
 * on mount, the answer the page below it had just given. Last writer wins is the bug; precedence is
 * the fix, and it is the same shape as the chain itself.
 */

/** What a full-page route has claimed, if one is on screen. */
let claimed = null

/** What the application's precedence chain last resolved. */
let appLanguage = null

function apply() {
  const next = claimed || appLanguage
  if (!next || typeof document === 'undefined') return
  document.documentElement.lang = next
}

/**
 * The application chain's answer — LanguageProvider, and nothing else.
 *
 * It LOSES to a claim rather than overwriting one, which is the whole reason this function exists
 * instead of an assignment.
 */
export function setAppDocumentLanguage(tag) {
  appLanguage = tag || null
  apply()
}

/**
 * A page that decides its own language says so here, and releases it when it leaves.
 *
 * Releasing hands the attribute back to the application chain rather than clearing it, so navigating
 * off the sticker sheet does not leave the rest of the app describing itself in the sheet's language.
 */
export function claimDocumentLanguage(tag) {
  claimed = tag || null
  apply()
}

export function releaseDocumentLanguage() {
  claimed = null
  apply()
}

/** Test seam: forget both, so one test's page cannot decide the next one's. */
export function resetDocumentLanguage() {
  claimed = null
  appLanguage = null
}
